/**
 * Drives the side-by-side analysis dashboard: two independently selectable
 * panels (keyed "a"/"b"), each letting the user pick an actor and choose
 * between viewing that actor's measures or its properties.
 *
 * Each panel (`.Module[data-module-key="a"|"b"]`) has two selects —
 * `#analysis_target_<key>` (which actor) and `#measures_or_properties_<key>`
 * (measures vs. properties) — and a `.Analysis__content-target` container
 * per panel that gets filled with the fetched HTML. That container holds
 * two slots: the first receives the primary content (the measures list, or
 * the selected actor's own properties), and the second is used only in the
 * properties case to show a compared actor's properties, toggled visible
 * via the "b" panel's content target.
 *
 * Used by `app/views/games/analysis/naheimst/_dashboard.html.erb` and
 * `app/views/games/analysis/nat_budget_negotiations/_dashboard.html.erb`.
 */

import { mountElementsInContainer } from "../vue/utils.js";
import filters from "./filters.js";
import measures from "./measures.js";

/**
 * Maps an actor id to the id of the article to fetch measures from when
 * that actor is selected as a "measures" analysis target.
 * @type {Record<string, number>}
 */
let actorToArticle = {};
/**
 * Maps an actor id to the property category ids to filter by when that
 * actor is selected as a "properties" analysis target.
 * @type {Record<string, number[]>}
 */
let actorToPropertyCategories = {};

/**
 * @param {Record<string, number> | null} newActorToArticle
 * @param {Record<string, number[]>} newActorToPropertyCategories
 */
function init(newActorToArticle, newActorToPropertyCategories) {
	actorToArticle = newActorToArticle ?? {};
	actorToPropertyCategories = newActorToPropertyCategories;

	$(
		"select#measures_or_properties_a, select#measures_or_properties_b, select#analysis_target_a, select#analysis_target_b",
	).on("change", changeAnalyticsView);
}

/** @param {JQuery.TriggeredEvent} event */
function changeAnalyticsView(event) {
	const $module = $(event.currentTarget).closest(".Module");
	const $targetContainer = $module.find(".Analysis__content-target");

	const key =
		$module.attr("data-module-key") ||
		$module.find("[data-module-key]").first().attr("data-module-key");
	const otherKeys = ["a", "b"].filter((arrKey) => {
		return arrKey !== key;
	});

	const selectedActor = $module.find(`#analysis_target_${key}`).val();

	if (typeof selectedActor !== "string" || !selectedActor) {
		return;
	}

	const showMeasure =
		$module.find(`#measures_or_properties_${key}`).val() === "measures";

	if (isAlreadySelected(otherKeys, selectedActor, showMeasure)) {
		$targetContainer.html(I18n.t("analysis.already_open"));
		return;
	}

	let url;
	if (showMeasure) {
		url = `articles/${actorToArticle[selectedActor]}/measures.json`;
	} else {
		url = `actors/${selectedActor}/properties.json`;
		if (actorToPropertyCategories?.[selectedActor]) {
			url +=
				`?category_ids=` +
				encodeURIComponent(
					JSON.stringify(actorToPropertyCategories[selectedActor]),
				);
		}
	}

	// @ts-expect-error
	$targetContainer.first().html(pp.loaderHtml);
	$targetContainer.eq(1).html("");

	$.get(url, (data) => {
		if (showMeasure) {
			const articleId = actorToArticle[selectedActor];
			$targetContainer.html(
				`<div class='MeasuresContainer j-for-article-${articleId}'>${data.measures_list}</div>`,
			);
			filters.init(`.j-for-article-${articleId}`);
			measures.initList(`.j-for-article-${articleId}`, $targetContainer);
		} else {
			$targetContainer.first().html(data.properties_stats_html);
			if (data.compare_properties_stats_html) {
				const $otherModule = $module.siblings(".Module");
				$targetContainer.eq(1).removeClass("hidden");
				$module
					.find("[data-module-key='b'] .Analysis__content-target")
					.html(data.compare_properties_stats_html);
				mountElementsInContainer($otherModule);
			} else if (actorToPropertyCategories) {
				$targetContainer.eq(1).addClass("hidden");
			}
			$targetContainer
				.parent()
				.toggleClass("col-md-6", data.set_md_cols == true);
		}
		mountElementsInContainer($targetContainer);
	});
}

/**
 * @param {string[]} otherKeys
 * @param {string} selectedActor
 * @param {boolean} showMeasure
 */
function isAlreadySelected(otherKeys, selectedActor, showMeasure) {
	const measureOrProperty = showMeasure ? "measures" : "properties";
	return otherKeys.some((key) => {
		const $container = $(`[data-module-key='${key}']`);
		const $option = $container.find(
			`option[value='${selectedActor}']:selected`,
		);
		const $typeOption = $container.find(
			`option[value='${measureOrProperty}']:selected`,
		);
		return $option.length > 0 && $typeOption.length > 0;
	});
}

export default { init };
